import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RefreshCw, MessageSquarePlus, Mail, Eye, Download } from "lucide-react";
import { goAdminList, goAdminUpdate, goDigestSend, goDigestPreview, goDigestRecipients, goDigestOpt, goAuditCsvUrl, goSetDigestTz } from "@/lib/api";
import { getToken } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "converted", "closed_lost"];
const STATUS_COLOR = {
  new: "bg-sky-100 text-sky-800 border-sky-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  qualified: "bg-violet-100 text-violet-800 border-violet-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed_lost: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminGrowthOperator() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [saving, setSaving] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [showDigestPanel, setShowDigestPanel] = useState(false);
  const [tzDraft, setTzDraft] = useState({});      // email -> tz string
  const [csvFrom, setCsvFrom] = useState("");
  const [csvTo, setCsvTo] = useState("");
  const ALL_COLS = [
    "task_id", "created_at", "event_at", "event", "by",
    "detail", "task_title", "task_status_current", "agent",
    "owner_email", "industry_pack", "priority",
  ];
  const [csvCols, setCsvCols] = useState(ALL_COLS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await goAdminList(filter || undefined);
      setRows(r.rows || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load. Admin only.");
    } finally { setLoading(false); }
  }, [filter]);

  const loadRecipients = useCallback(async () => {
    try {
      const r = await goDigestRecipients();
      setRecipients(r.recipients || []);
    } catch { /* silently ignore for non-admin */ }
  }, []);

  useEffect(() => { load(); loadRecipients(); }, [load, loadRecipients]);

  const toggleOptIn = async (email, opt_in) => {
    try {
      await goDigestOpt(email, opt_in);
      toast.success(opt_in ? "Digest turned on" : "Digest turned off");
      await loadRecipients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveTz = async (email) => {
    const tz = (tzDraft[email] || "").trim();
    if (!tz) return;
    try {
      await goSetDigestTz(email, tz);
      toast.success(`Timezone set to ${tz}`);
      setTzDraft((p) => { const n = { ...p }; delete n[email]; return n; });
      await loadRecipients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid IANA timezone");
    }
  };

  const downloadAuditCsv = () => {
    const token = getToken();
    if (!token) return toast.error("Sign in as admin first");
    fetch(goAuditCsvUrl(csvFrom, csvTo, csvCols), {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error("CSV failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = [csvFrom || "all", csvTo || "now"].join("-to-");
      a.href = url; a.download = `growth-tasks-audit-${suffix}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Download failed"));
  };

  // TZ auto-detect: first time the digest panel opens, silently propose the
  // browser's resolved timezone for each admin who is still on UTC. We only
  // persist for the *current* admin's own row to avoid moving someone else's
  // schedule without consent — everyone else just sees the suggestion prefilled
  // in the input and can save on blur.
  useEffect(() => {
    if (!showDigestPanel || !recipients.length) return;
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected || detected === "UTC") return;
    setTzDraft((prev) => {
      const next = { ...prev };
      for (const r of recipients) {
        if ((r.tz || "UTC") === "UTC" && !next[r.email]) {
          next[r.email] = detected;
        }
      }
      return next;
    });
  }, [showDigestPanel, recipients]);

  const setStatus = async (bid, status) => {
    setSaving({ ...saving, [bid]: true });
    try {
      const note = noteDraft[bid]?.trim();
      await goAdminUpdate(bid, { status, note: note || undefined });
      toast.success(`Marked ${status.replace("_", " ")}`);
      setNoteDraft({ ...noteDraft, [bid]: "" });
      await load();
    } catch (e) {
      toast.error("Update failed");
    } finally { setSaving({ ...saving, [bid]: false }); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-go">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <h1 className="font-heading text-3xl font-black text-slate-900">Growth Operator · Blueprint backlog</h1>
          <p className="mt-1 text-sm text-slate-500">Every submitted Growth Blueprint lands here. Move leads through New → Contacted → Qualified → Converted.</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant={filter === "" ? "default" : "outline"} onClick={() => setFilter("")} data-testid="go-admin-filter-all">
              All <span className="ml-1 opacity-60">({Object.values(counts).reduce((a, b) => a + b, 0)})</span>
            </Button>
            {STATUSES.map(s => (
              <Button
                key={s} size="sm"
                variant={filter === s ? "default" : "outline"}
                onClick={() => setFilter(s)}
                data-testid={`go-admin-filter-${s}`}
              >
                <span className="capitalize">{s.replace("_", " ")}</span>
                <span className="ml-1 opacity-60">({counts[s] || 0})</span>
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={load} data-testid="go-admin-refresh">
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => setShowDigestPanel(v => !v)}
              data-testid="go-admin-digest-recipients-toggle"
            >
              <Mail className="mr-1 h-3.5 w-3.5" /> Recipients ({recipients.filter(r => r.opt_in).length}/{recipients.length})
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={downloadAuditCsv}
              data-testid="go-admin-audit-csv"
            >
              <Download className="mr-1 h-3.5 w-3.5" /> Audit CSV
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={async () => {
                try {
                  const r = await goDigestPreview();
                  const w = window.open("", "_blank");
                  w.document.write(`<html><head><title>Digest preview · ${r.week_of}</title></head><body>${r.html}</body></html>`);
                } catch { toast.error("Preview failed"); }
              }}
              data-testid="go-admin-digest-preview"
            >
              <Eye className="mr-1 h-3.5 w-3.5" /> Preview digest
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm("Send the weekly digest to opted-in admins?")) return;
                try {
                  const r = await goDigestSend(false);
                  toast.success(`Digest queued · ${r.sent.length} sent · ${r.errors.length} errors`);
                } catch { toast.error("Send failed"); }
              }}
              className="bg-orange-500 text-white hover:bg-orange-400"
              data-testid="go-admin-digest-send"
            >
              <Mail className="mr-1 h-3.5 w-3.5" /> Send digest now
            </Button>
          </div>
        </div>
      </section>

      {showDigestPanel && (
        <section className="mx-auto max-w-7xl px-4 py-6" data-testid="go-admin-digest-panel">
          <Card className="border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-lg font-bold text-slate-900">Weekly digest recipients</div>
                <div className="text-xs text-slate-500">Admins receive the Monday-morning digest by default. Toggle any admin off to opt them out — the change takes effect immediately.</div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {recipients.length === 0 && <div className="text-sm text-slate-500">No admin users found.</div>}
              {recipients.map((r) => (
                <div key={r.email} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`go-admin-recipient-${r.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{r.name || r.email}</div>
                    <div className="text-xs text-slate-500">{r.email} · {r.role}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-xs text-slate-700">
                      <span className="font-semibold text-slate-500 uppercase tracking-wider">TZ</span>
                      <Input
                        data-testid={`go-admin-tz-input-${r.email}`}
                        className="h-7 w-44 text-xs font-mono"
                        placeholder="e.g. America/New_York"
                        value={tzDraft[r.email] ?? r.tz}
                        onChange={(e) => setTzDraft({ ...tzDraft, [r.email]: e.target.value })}
                        onBlur={() => {
                          if (tzDraft[r.email] && tzDraft[r.email] !== r.tz) saveTz(r.email);
                        }}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                      <Switch
                        data-testid={`go-admin-optin-switch-${r.email}`}
                        checked={r.opt_in}
                        onCheckedChange={(v) => toggleOptIn(r.email, !!v)}
                      />
                      {r.opt_in ? "Receiving digest" : "Opted out"}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 border-t border-slate-100 pt-4">
              <div className="font-heading text-sm font-bold text-slate-900">Audit log export</div>
              <div className="mt-1 text-xs text-slate-500">Download every task history event (create · approve · request changes · reject · edit_draft) as CSV. Leave both dates empty for a full export.</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  From
                  <Input type="date" value={csvFrom} onChange={(e) => setCsvFrom(e.target.value)}
                    className="h-8 w-40 text-xs" data-testid="go-admin-audit-from" />
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  To
                  <Input type="date" value={csvTo} onChange={(e) => setCsvTo(e.target.value)}
                    className="h-8 w-40 text-xs" data-testid="go-admin-audit-to" />
                </div>
                <Button size="sm" onClick={downloadAuditCsv} data-testid="go-admin-audit-csv-panel"
                  className="bg-slate-900 text-white hover:bg-slate-800">
                  <Download className="mr-1 h-3.5 w-3.5" /> Download CSV
                </Button>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <span>Columns ({csvCols.length}/{ALL_COLS.length})</span>
                  <div className="flex gap-3">
                    <button type="button"
                      className="hover:text-orange-600"
                      onClick={() => setCsvCols(ALL_COLS)}
                      data-testid="go-admin-csv-cols-all">Select all</button>
                    <button type="button"
                      className="hover:text-orange-600"
                      onClick={() => setCsvCols(["task_id", "event_at", "event", "by", "task_title"])}
                      data-testid="go-admin-csv-cols-minimal">Minimal preset</button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3 md:grid-cols-4">
                  {ALL_COLS.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-[11px] text-slate-700"
                      data-testid={`go-admin-csv-col-${c}`}>
                      <input
                        type="checkbox"
                        checked={csvCols.includes(c)}
                        onChange={(e) => {
                          setCsvCols(e.target.checked
                            ? [...csvCols, c]
                            : csvCols.filter((x) => x !== c));
                        }}
                      />
                      <span className="font-mono">{c}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-6 space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <Card className="border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="go-admin-empty">
            No blueprints in this stage yet.
          </Card>
        )}
        {rows.map(r => {
          const open = expanded === r.id;
          return (
            <Card key={r.id} className="border-slate-200 bg-white p-5" data-testid={`go-admin-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-heading text-base font-bold text-slate-900">{r.business_name}</div>
                    <Badge className={STATUS_COLOR[r.status] || STATUS_COLOR.new}>{r.status.replace("_", " ")}</Badge>
                    {r.wants_human && <Badge className="bg-orange-100 text-orange-800 border-orange-200">Wants human</Badge>}
                    <Badge variant="outline" className="capitalize">{r.industry_pack.replace("-", " ")}</Badge>
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {r.contact_name} · <a className="hover:text-orange-600" href={`mailto:${r.contact_email}`}>{r.contact_email}</a>
                    {r.contact_phone && <> · {r.contact_phone}</>}
                    {r.website && <> · <a className="text-orange-600 hover:underline" href={r.website} target="_blank" rel="noreferrer">{r.website}</a></>}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">Readiness {r.readiness}/100 · Recommended tier: <b>{r.recommended_tier_name}</b> · {new Date(r.created_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.filter(s => s !== r.status).map(s => (
                    <Button
                      key={s} size="sm" variant="outline"
                      onClick={() => setStatus(r.id, s)}
                      disabled={saving[r.id]}
                      data-testid={`go-admin-mark-${s}-${r.id}`}
                    >
                      {saving[r.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : `→ ${s.replace("_", " ")}`}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="mt-3">
                <Button size="sm" variant="ghost" onClick={() => setExpanded(open ? null : r.id)} data-testid={`go-admin-toggle-${r.id}`}>
                  {open ? "Hide details" : `Details · ${r.notes?.length || 0} notes`}
                </Button>
              </div>

              {open && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-3" data-testid={`go-admin-details-${r.id}`}>
                  {r.notes?.length > 0 && (
                    <div className="space-y-2">
                      {r.notes.map((n, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                          <div className="text-slate-500">{new Date(n.at).toLocaleString()} · {n.by}</div>
                          <div className="mt-1 whitespace-pre-wrap text-slate-800">{n.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Textarea
                    data-testid={`go-admin-note-${r.id}`}
                    value={noteDraft[r.id] || ""}
                    onChange={(e) => setNoteDraft({ ...noteDraft, [r.id]: e.target.value })}
                    placeholder="Add an internal note before advancing the stage…"
                    className="min-h-[80px]"
                  />
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <MessageSquarePlus className="h-3.5 w-3.5" /> Notes attach to the next stage change.
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </section>
      <Footer />
    </div>
  );
}
